import {AssetReport, ImportReportDetail, REPORT_DETAIL_STATUS} from '../../../utils/dataImport';
import {Col, Pagination, Row, Table, Tag} from 'antd';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useLazyGetImportReportQuery} from '../../../redux/api/importReport';
import {useNavigate, useParams} from 'react-router-dom';
import ImportReportDetailModal from '../../../pages/settings/importReport/ImportReportDetailModal';
import {ColumnsType} from 'antd/lib/table';
import _, {capitalize} from 'lodash';
import {getTranslationAssetTableColumnKey} from '../../../utils/translation';
import {
  removeImportReportFilter,
  selectImportReport,
  setImportReportFilters,
  setImportReportPagination,
  setImportReportSorters,
} from '../../../redux/slices/importReport';
import {DocumentsTableColumnHeader} from '../../documents/TablesViews/DocumentsTableColumnHeader';
import TableSorter from '../../common/TableSorter';
import {fromFeToBeSorter} from '../../../utils/parser';
import {fromFeToBeImportReportTableFilters,} from "../../../types/internal/ImportReportTable";
import ImportReportRelatedOptionsFilter from "./ImportReportRelatedOptionsFilter";

export const ImportReportTableView = ({reportStatus}: { reportStatus: REPORT_DETAIL_STATUS }) => {

  const {t} = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {uuid: reportUuid} = useParams();

  const [
    modalData,
    setModalData,
  ] = useState<null | { asset: AssetReport, detail: string }>(null);

  const pagination = useSelector(selectImportReport)[reportStatus].pagination;
  const sorters = useSelector(selectImportReport)[reportStatus].sorters;
  const filters = useSelector(selectImportReport)[reportStatus].filters;

  const [
    getData,
    {
      data,
      isLoading,
      isFetching,
    },
  ] = useLazyGetImportReportQuery();

  useEffect(() => {
    if (reportUuid) {
      getData({
        page: pagination.current,
        page_size: pagination.pageSize,
        report_uuid: reportUuid,
        status: reportStatus,
        ...fromFeToBeImportReportTableFilters(filters),
        ...(!_.isEmpty(fromFeToBeSorter(sorters)) ? {
          ordering: fromFeToBeSorter(sorters),
        } : {}),
      });
    }
  }, [filters, getData, pagination, reportUuid, sorters, reportStatus]);

  const columns: ColumnsType<ImportReportDetail> = [
    {
      title: (
        <DocumentsTableColumnHeader
          title={capitalize(t(getTranslationAssetTableColumnKey('sigi_code')))}
          sorter={<TableSorter
            value={sorters.sigi_code}
            onChange={type => dispatch(setImportReportSorters({sigi_code: type, status: reportStatus}))}
          />}
          filter={reportUuid && <ImportReportRelatedOptionsFilter
            status={reportStatus}
            filters={filters}
            setFilters={(params) => {
              dispatch(setImportReportFilters({status: reportStatus, ...params}))
            }}
            field={"sigi_code"}
            reportUuid={reportUuid}
          />}
          onFilterReset={() => dispatch(removeImportReportFilter({status: reportStatus, filter: 'sigi_code'}))}
          isFilterDisabled={filters.sigi_code === undefined}
        />
      ),
      key: 'sigi_code',
      dataIndex: ['asset', 'sigi_code'],
    },
    {
      key: 'country',
      title: (
        <DocumentsTableColumnHeader
          title={capitalize(t(getTranslationAssetTableColumnKey('country')))}
          sorter={<TableSorter
            value={sorters.country}
            onChange={type => dispatch(setImportReportSorters({country: type, status: reportStatus}))}
          />}
          filter={reportUuid && <ImportReportRelatedOptionsFilter
            status={reportStatus}
            filters={filters}
            setFilters={(params) => {
              dispatch(setImportReportFilters({status: reportStatus, ...params}))
            }}
            field={"country"}
            reportUuid={reportUuid}
          />}
          onFilterReset={() => dispatch(removeImportReportFilter({status: reportStatus, filter: 'country'}))}
          isFilterDisabled={filters.country === undefined}
        />
      ),
      dataIndex: ['asset', 'country'],
    },
    {
      key: 'admin_level_1',
      title: (
        <DocumentsTableColumnHeader
          title={capitalize(t(getTranslationAssetTableColumnKey('admin_level_1')))}
          sorter={<TableSorter
            value={sorters.admin_level_1}
            onChange={type => dispatch(setImportReportSorters({admin_level_1: type, status: reportStatus}))}
          />}
          filter={reportUuid && <ImportReportRelatedOptionsFilter
            status={reportStatus}
            filters={filters}
            setFilters={(params) => {
              dispatch(setImportReportFilters({status: reportStatus, ...params}))
            }}
            field={"admin_level_1"}
            reportUuid={reportUuid}
          />}
          onFilterReset={() => dispatch(removeImportReportFilter({status: reportStatus, filter: 'admin_level_1'}))}
          isFilterDisabled={filters.admin_level_1 === undefined}
        />
      ),
      dataIndex: ['asset', 'admin_level_1'],
      render: (value) => <div style={{textTransform: 'capitalize'}}>{value ? value.toLowerCase() : ''}</div>,
    },
    {
      key: 'admin_level_2',
      title: (
        <DocumentsTableColumnHeader
          title={capitalize(t(getTranslationAssetTableColumnKey('admin_level_2')))}
          sorter={<TableSorter
            value={sorters.admin_level_2}
            onChange={type => dispatch(setImportReportSorters({admin_level_2: type, status: reportStatus}))}
          />}
          filter={reportUuid && <ImportReportRelatedOptionsFilter
            status={reportStatus}
            filters={filters}
            setFilters={(params) => {
              dispatch(setImportReportFilters({status: reportStatus, ...params}))
            }}
            field={"admin_level_2"}
            reportUuid={reportUuid}
          />}
          onFilterReset={() => dispatch(removeImportReportFilter({status: reportStatus, filter: 'admin_level_2'}))}
          isFilterDisabled={filters.admin_level_2 === undefined}
        />
      ),
      dataIndex: ['asset', 'admin_level_2'],
    },
    {
      key: 'admin_level_3',
      title: (
        <DocumentsTableColumnHeader
          title={capitalize(t(getTranslationAssetTableColumnKey('admin_level_3')))}
          sorter={<TableSorter
            value={sorters.admin_level_3}
            onChange={type => dispatch(setImportReportSorters({admin_level_3: type, status: reportStatus}))}
          />}
          filter={reportUuid && <ImportReportRelatedOptionsFilter
            status={reportStatus}
            filters={filters}
            setFilters={(params) => {
              dispatch(setImportReportFilters({status: reportStatus, ...params}))
            }}
            field={"admin_level_3"}
            reportUuid={reportUuid}
          />}
          onFilterReset={() => dispatch(removeImportReportFilter({status: reportStatus, filter: 'admin_level_3'}))}
          isFilterDisabled={filters.admin_level_3 === undefined}
        />
      ),
      dataIndex: ['asset', 'admin_level_3'],
      render: (value) => <div style={{textTransform: 'capitalize'}}>{value ? value.toLowerCase() : ''}</div>,
    },
    {
      key: 'address',
      title: (
        <DocumentsTableColumnHeader
          title={capitalize(t(getTranslationAssetTableColumnKey('address')))}
          sorter={<TableSorter
            value={sorters.address}
            onChange={type => dispatch(setImportReportSorters({address: type, status: reportStatus}))}
          />}
          filter={reportUuid && <ImportReportRelatedOptionsFilter
            status={reportStatus}
            filters={filters}
            setFilters={(params) => {
              dispatch(setImportReportFilters({status: reportStatus, ...params}))
            }}
            field={"address"}
            reportUuid={reportUuid}
          />}
          onFilterReset={() => dispatch(removeImportReportFilter({status: reportStatus, filter: 'address'}))}
          isFilterDisabled={filters.address === undefined}
        />
      ),
      dataIndex: ['asset', 'address'],
      render: (value) => <div style={{textTransform: 'capitalize'}}>{value ? value.toLowerCase() : ''}</div>,
    },
    {
      key: 'operation',
      title: (
        <DocumentsTableColumnHeader
          title={capitalize(t('dataImport.operation'))}
          sorter={<TableSorter
            value={sorters.operation}
            onChange={type => dispatch(setImportReportSorters({operation: type, status: reportStatus}))}
          />}
          filter={reportUuid && <ImportReportRelatedOptionsFilter
            status={reportStatus}
            filters={filters}
            setFilters={(params) => {
              dispatch(setImportReportFilters({status: reportStatus, ...params}))
            }}
            field={"operation"}
            reportUuid={reportUuid}
          />}
          onFilterReset={() => dispatch(removeImportReportFilter({status: reportStatus, filter: 'operation'}))}
          isFilterDisabled={filters.operation === undefined}
        />
      ),
      dataIndex: 'operation',
      render: value => {
        switch (value) {
          case 'CREATE':
            return capitalize(t('dataImport.creation', {count: 1}));
          case 'UPDATE':
            return capitalize(t('dataImport.update', {count: 1}));
          default:
            return '';
        }
      },
    },
    ...(reportStatus !== "OK") ? [{
      key: 'is_resolved',
      title: (
        <DocumentsTableColumnHeader
          title={capitalize(t('dataImport.isResolved'))}
          sorter={<TableSorter
            // @ts-ignore
            value={sorters.is_resolved}
            onChange={type => dispatch(setImportReportSorters({is_resolved: type, status: reportStatus}))}
          />}

          filter={reportUuid && <ImportReportRelatedOptionsFilter
            status={reportStatus}
            filters={filters}
            setFilters={(params) => {
              dispatch(setImportReportFilters({status: reportStatus, ...params}))
            }}
            field={"is_resolved"}
            reportUuid={reportUuid}
          />}
          onFilterReset={() => dispatch(removeImportReportFilter({status: reportStatus, filter: ('is_resolved')}))}
          // @ts-ignore
          isFilterDisabled={filters.is_resolved === undefined}
        />
      ),
      dataIndex: 'is_resolved',
      render: (value: boolean | null) => {
        if (typeof value === 'boolean')
          return value ?
            <Tag color={'success'}>{capitalize(t('misc.yes'))}</Tag> :
            <Tag color={'error'}>{capitalize(t('misc.no'))}</Tag>;
      },
    }] : [],
  ];

  return (<>
    {!!modalData && !!reportUuid && (
      <ImportReportDetailModal
        detailUuid={modalData?.detail}
        reportUuid={reportUuid}
        modal={{
          open: !!modalData && !!reportUuid,
          onCancel: () => setModalData(null),
        }}
        assetButton={{
          onClick: () => navigate(`/assets/${modalData?.asset.uuid}/edit`),
        }}
        closeModalButton={{
          onClick: () => setModalData(null),
        }}
        title={{
          children: modalData?.asset.sigi_code,
        }}
      />
    )}
    <Row>
      <Col flex={1}>
        <Table
          <ImportReportDetail>
          columns={columns}
          pagination={false}
          dataSource={data?.results || []}
          rowKey={'uuid'}
          loading={isLoading || isFetching}
          scroll={{x: true}}
          onRow={(record) => (
            {
              onClick: (e) => {
                setModalData({detail: record.uuid, asset: record.asset});
              },
              style: {
                cursor: 'pointer',
              },
            }
          )
          }
        />
      </Col>
    </Row>
    <Row
      style={{marginTop: '1rem'}}
      justify={'center'}
    >
      <Col>
        <Pagination
          disabled={!data?.count}
          showSizeChanger={true}
          total={data?.count}
          showTotal={(total, range) => t('table.pageSizeOfTotal', {
            rangeStart: range[0],
            rangeEnd: range[1],
            total,
          })}
          onChange={(current, pageSize) => {
            dispatch(setImportReportPagination({status: reportStatus, current, pageSize}));
          }}
          {...pagination}
        />
      </Col>
    </Row>
  </>);
};
